import { FC, useCallback } from "react";

import Stack from "@mui/material/Stack";
import { canFormCategoryGenerateReceipt, getAddOnRates, getTicketRates } from "@simplyk/common";
import { FormProvider, useForm, useWatch } from "react-hook-form";

import { AddOnsList } from "./AddOnsList/AddOnsList";
import { mapDataToEditAddOns } from "./helpers/mapDataToEditAddOns";
import { Section } from "./Section/Section";
import { StepAddOnsFormData } from "./types";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { mapRateOutputsToRateData } from "@/common/helpers/form-edition";
import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";
import { RateByOccurrenceOutput, RateOutput } from "@/common/types/trpc";
import { LiveEditorBlock } from "@/components/LiveEditorElements/LiveEditorBlock/LiveEditorBlock";
import { LiveEditorToggle } from "@/components/LiveEditorElements/LiveEditorToggle/LiveEditorToggle";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { ScrollContext } from "@/features/LiveFormEditor/AnimatePreview/getScrollTopStepTarget";
import { useLiveFormAmplitudeEvents } from "@/features/LiveFormEditor/common/amplitude/useLiveFormAmplitudeEvents";
import { useLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import { useUpdateTicketingDataInPreview } from "@/features/LiveFormEditor/common/useUpdateLiveTicketingPreview/useUpdateTicketingDataInPreview";
import { FormStepContainer } from "@/features/LiveFormEditor/Steps/FormStepContainer";
import { TICKETING_SELECTION_V2_RATE_ID_PREFIX } from "@/features/LiveFormEditor/Steps/helpers/formStepToElementIdMap";
import { useUploadRemainingRatePhotos } from "@/features/LiveFormEditor/Steps/helpers/useUploadRemainingRatePhotos";
import {
  RateImageEditorsProvider,
  useRateImageEditorsRegistry,
} from "@/features/LiveFormEditor/Steps/StepTickets/Rates/RateImageEditors/RateImageEditorsContext";
import { RatesDigestProvider } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/RatesDigest/RatesDigestContext";
import { useFormSaveAndNavigate } from "@/features/LiveFormEditor/Steps/useFormSaveAndNavigate";

export const TicketingLiveEditorStepAddOns: FC = () => {
  const { t } = useTranslate();
  const { organization } = useCurrentUserContext();
  const { apiFormObject: apiTicketing, liveFormData } = useLiveFormEditorContext<"ticketing">();
  const { logUserActionEvent } = useLiveFormAmplitudeEvents();

  const canFormGenerateReceipt = canFormCategoryGenerateReceipt(organization?.country, apiTicketing.formCategory);

  const defaultValues: StepAddOnsFormData = {
    rates: mapRateOutputsToRateData(getAddOnRates(apiTicketing.rates), apiTicketing.locale).map((addOn) => ({
      ...addOn,
      photos: addOn.photos?.length ? addOn.photos : [{ photoUrl: null, id: crypto.randomUUID() }],
    })),
    ratesToDelete: [],
    imageIsUploading: false,
    extraDonation: apiTicketing.extraDonation,
    generateExtraDonationReceipt:
      Boolean(organization?.canGenerateReceipt) &&
      canFormGenerateReceipt &&
      apiTicketing.extraDonation &&
      apiTicketing.generateExtraDonationReceipt,
    previewSectionInFocus: null,
  };

  const form = useForm<StepAddOnsFormData>({ defaultValues });
  const isUploading = useWatch({ control: form.control, name: "imageIsUploading" });

  const { registryValue: rateImageEditorsRegistry, finalizePendingImageEditors } = useRateImageEditorsRegistry();

  const updateTicketingAddOns = trpc.updateTicketingAddOns.useMutation();
  const uploadRemainingRatePhotos = useUploadRemainingRatePhotos();

  const { handleSaveAndNext, handleSaveAndBack } = useFormSaveAndNavigate<StepAddOnsFormData>({
    type: "ticketing",
    form,
    saveData: async () => {
      await finalizePendingImageEditors();
      await uploadRemainingRatePhotos({
        rates: form.getValues("rates"),
        setPhotoUrl: (addOnIndex, photoIndex, photoUrl) =>
          form.setValue(`rates.${addOnIndex}.photos.${photoIndex}.photoUrl` as const, photoUrl, { shouldDirty: true }),
      });

      const formData = form.getValues();

      return await updateTicketingAddOns.mutateAsync({
        ticketingId: apiTicketing.id,
        extraDonation: formData.extraDonation,
        generateExtraDonationReceipt: formData.extraDonation && formData.generateExtraDonationReceipt,
        addOns: mapDataToEditAddOns(formData.rates, apiTicketing),
        addOnsToDelete: formData.ratesToDelete,
      });
    },
  });

  const isExtraDonationActive = useWatch({ control: form.control, name: "extraDonation" });
  const displayGenerateExtraDonationReceipt = Boolean(organization?.canGenerateReceipt) && canFormGenerateReceipt;
  const displayGiftAidExplanation = organization?.hasConnectedSwiftaidAtUtc;

  const previewSectionInFocus = useWatch({ control: form.control, name: "previewSectionInFocus" });
  const firstAddOnIdInPreview = getAddOnRates(liveFormData?.ticketing.rates ?? [])[0]?.id;

  const getScrollContext = useCallback(
    (): ScrollContext =>
      previewSectionInFocus !== "additionalDonation" && firstAddOnIdInPreview
        ? { targetElementId: `${TICKETING_SELECTION_V2_RATE_ID_PREFIX}${firstAddOnIdInPreview}` }
        : {},
    [previewSectionInFocus, firstAddOnIdInPreview]
  );

  useUpdateTicketingDataInPreview({
    form,
    mutate: (formData, liveDataToMutate) => {
      liveDataToMutate.addOnsPreviewSection = formData.previewSectionInFocus;
      liveDataToMutate.ticketing.extraDonation = true;
      liveDataToMutate.ticketing.generateExtraDonationReceipt =
        formData.extraDonation && formData.generateExtraDonationReceipt;
      liveDataToMutate.placeholders.isExtraDonation = !formData.extraDonation;

      const draftAddOnRates = mapDataToEditAddOns(formData.rates, apiTicketing).map(
        (addOn) => ({ ...addOn, isAddOn: true, photoUrls: addOn.photos }) as unknown as RateOutput
      );
      const ticketRates = getTicketRates(liveDataToMutate.ticketing.rates);
      liveDataToMutate.ticketing.rates = [...ticketRates, ...draftAddOnRates];

      if (liveDataToMutate.selectedOccurrenceWithRates) {
        const draftAddOnRateIds = new Set(draftAddOnRates.map((rate) => rate.id));
        const savedAddOnRateIds = new Set(getAddOnRates(apiTicketing.rates).map((rate) => rate.id));
        const ticketOccurrenceRates = liveDataToMutate.selectedOccurrenceWithRates.rates.filter(
          (occurrenceRate) =>
            !savedAddOnRateIds.has(occurrenceRate.rateId) && !draftAddOnRateIds.has(occurrenceRate.rateId)
        );
        liveDataToMutate.selectedOccurrenceWithRates.rates = [
          ...ticketOccurrenceRates,
          ...draftAddOnRates.map(
            (addOn) =>
              ({
                rateId: addOn.id,
                remainingTickets: addOn.seats ?? null,
                soldTicketsCount: 0,
              }) as RateByOccurrenceOutput
          ),
        ];
      }
    },
  });

  return (
    <FormStepContainer
      title={t("dashboard", "ticketing.addOns.stepTitle")}
      form={form}
      getScrollContext={getScrollContext}
      actionsProps={{
        onBack: handleSaveAndBack,
        onNext: handleSaveAndNext,
        disableNext: form.formState.isSubmitting || isUploading,
        disableBack: form.formState.isSubmitting || isUploading,
      }}
    >
      <FormProvider {...form}>
        <Stack spacing={6} sx={{ marginTop: 4 }}>
          <Section
            title={t("dashboard", "ticketing.addOns.additionalDonationsSectionTitle")}
            description={t("dashboard", "ticketing.addOns.additionalDonationsSectionDescription")}
            onInteract={() => form.setValue("previewSectionInFocus", "additionalDonation")}
            data-test="add-ons-step-additional-donation-section"
          >
            <LiveEditorBlock spacing={2}>
              <LiveEditorToggle
                label={t("dashboard", "ticketing.addOns.additionalDonations")}
                name="extraDonation"
                control={form.control}
                onValueChange={(checked) => {
                  if (!checked) {
                    form.setValue("generateExtraDonationReceipt", false, { shouldDirty: true });
                  }
                  logUserActionEvent(
                    checked ? AmplitudeEvents.FormExtraDonationAdded : AmplitudeEvents.FormExtraDonationRemoved
                  );
                }}
                data-test="ticketing-rates-extra-donation-switch"
              />
              {displayGenerateExtraDonationReceipt && (
                <LiveEditorToggle
                  label={t("dashboard", "ticketing.addOns.donationTaxReceipt")}
                  name="generateExtraDonationReceipt"
                  control={form.control}
                  disabled={!isExtraDonationActive}
                  data-test="ticketing-rates-extra-donation-receipt-switch"
                />
              )}
            </LiveEditorBlock>

            {displayGiftAidExplanation && (
              <DashboardTypography vibe="text-neutral-moderate" variant="body2">
                {t("dashboard", "ticketing.collectDonation.taxReceipt.giftaid")}
              </DashboardTypography>
            )}
          </Section>

          <Section
            title={t("dashboard", "ticketing.addOns.addOnsSectionTitle")}
            description={t("dashboard", "ticketing.addOns.addOnsSectionDescription")}
            onInteract={() => form.setValue("previewSectionInFocus", "addOns")}
            data-test="add-ons-step-add-ons-section"
          >
            <RateImageEditorsProvider value={rateImageEditorsRegistry}>
              <RatesDigestProvider>
                <AddOnsList form={form} />
              </RatesDigestProvider>
            </RateImageEditorsProvider>
          </Section>
        </Stack>
      </FormProvider>
    </FormStepContainer>
  );
};
